// -----------------------------------------------------------------------
// <copyright file="CentroFeedbackFunctions.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import {
  bootstrapFeature,
  ConfigUpdatedListener,
  DataSourceSetter,
  initialize,
  preloadFeature,
  RequestFunction,
} from '@ms/centro-hvc-loader';
import { IRuntimeHvcStaticConfig } from '@ms/centro-hvc-loader/dist/runtime/configuration/runtimeDataSources';
import { appContext } from '../../models/getHelp/appContext';
import { getHelpLogger } from '../getHelpLogger';
import { IFeedbackInitOptions } from './FeedbackTypes';
import { InitialFeedbackData } from './Initparameters';

// decalring as variable to reuse
const hvcName: string = 'ocv-inapp-feedback';
// setting an unmount funtion
/* eslint-disable-next-line @typescript-eslint/no-empty-function */
let unmountFunction = () => {};
export let centroInitSuccess: boolean = false;

// Intial Data to be passed to the Feedback HVC
const feedbackData: IFeedbackInitOptions = InitialFeedbackData;

export const isCentroInitialized = (): boolean => {
  return centroInitSuccess;
};

// This is the object which is used to open/close/update data to feedback
export const InAppFeedbackInitOptions = (() => {
  // Unmounting/removing any previous instances
  unmountFunction();
  /*
  Register and Unregister is part of the boilerplate code to enable dynamic data passing
   */
  const register = (
    listener?: ConfigUpdatedListener<IFeedbackInitOptions>
  ): Readonly<Partial<IFeedbackInitOptions>> => {
    if (listener) {
      listeners.push(listener);
    }
    return feedbackData;
  };

  const unregister = (listener: ConfigUpdatedListener<IFeedbackInitOptions>): void => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };

  const listeners: ConfigUpdatedListener<IFeedbackInitOptions>[] = [];
  /*
  This is the list of function provided to update the data passed to Feedback
   */
  const updateFeedbackObject = (updatedInAppFeedbackInitOptions: IFeedbackInitOptions) => {
    Object.assign(feedbackData, updatedInAppFeedbackInitOptions);
    listeners.forEach((listener: ConfigUpdatedListener<IFeedbackInitOptions>) => listener(feedbackData));
  };

  const configObject = {
    register,
    unregister,
    updateFeedbackObject,
  };
  return configObject;
})();

export const initializeCentro = async (locale: string, isGetHelp: boolean) => {
  try {
    const clientName = isGetHelp ? 'GetHelpApp' : 'SMC'; // Name of your product registed with Centro, OCV and ODS.
    const language = convertGetHelpLocaleToOCVLocale(locale);

    const dataSources = (sd: DataSourceSetter) => {
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      sd('centro.hvc.feedback.initOptions', InAppFeedbackInitOptions as any);
    };

    // Set RuntimeConfig
    const runtimeStaticConfig: IRuntimeHvcStaticConfig = {
      // This configures some static aspects of the Centro runtime.
      // The hosting app name, if you don't have a name, contact centro to add.
      hostName: clientName,

      // The locale of the HVC to be load, in BCP-47 format
      locale: language,

      // The AppId is not authentication related, it is a GUID that should be changed on every page reload for logging purpose.
      // You can generate it on your server-side, or in browser. Here we are generating it from server-side.
      appId: appContext.sessionId,

      // All perf log will be prefixed with this string. This is so that when we analysis perf logs,we know that those logs are coming
      // from HVC running in your environment.
      perfPrefix: `OcvFeedback${clientName}`,
    };

    const InitializationParameters = {
      centroEnvironmentBaseUrl: 'https://admin.microsoft.com',
      centroAdapters: {
        // This configures some static aspects of the Centro runtime.
        runtimeStaticConfig,
        /* eslint-disable-next-line @typescript-eslint/no-empty-function */
        request: (() => {}) as unknown as RequestFunction,
      },
      centroHvcData: '',
      /**
       * Request HVC versions for a particular version of hosting-app that is flighted in Centro versioning server.
       * This will sent the latest compatible version for Major release 1 with minor upgrade and bug fixes for eg 1.2.3
       */
      forHostVersion: 'v2', // This will retrieve Major version 2 for the HVC
    };

    await initialize(InitializationParameters);

    await preloadFeature({ featureName: hvcName });

    const bootstrapper = await bootstrapFeature({
      featureName: hvcName,
      setAdditionalDataSource: dataSources,
    });

    // use the main bootstrapper as only main is provided
    unmountFunction = await bootstrapper.main(document.getElementById('centroRef') as HTMLDivElement);
    getHelpLogger.logOCVFeedbackSDKInitSuccess();
    centroInitSuccess = true;
  } catch (error) {
    getHelpLogger.logOCVFeedbackSDKInitError(error);
  }
};

const convertGetHelpLocaleToOCVLocale = (locale: string) => {
  const mainLocale = locale.split('-', 1)[0];
  const subLocale = locale.substring(0, locale.lastIndexOf('-'));
  if (locale === 'en-GB' || locale === 'es-MX' || locale === 'fr-CA') {
    return locale;
  } else if (mainLocale === 'nb') {
    return 'nb-NO';
  } else if (locale === 'pt-br') {
    return 'pt-BR';
  } else if (mainLocale === 'pt') {
    return 'pt-PT';
  } else if (mainLocale === 'sr') {
    return 'sr-Latn-RS';
  } else if (subLocale === 'zh-Hant' || locale === 'zh-Hant' || locale === 'zh-HK' || locale === 'zh-TW') {
    return 'zh-Hant';
  } else if (mainLocale === 'zh') {
    return 'zh-Hans';
  } else {
    return mainLocale;
  }
};
